import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CancelIcon from "@mui/icons-material/Cancel";
import { getOrderDetail, updateOrderStatus } from "../../api/adminService";
import {
  DeliveryStatusLabels,
  OrderStatusColors,
  OrderStatusLabels,
  formatCurrency,
  formatDate,
} from "./constants";

const TERMINAL_STATUSES = [6, 7, 8]; // Delivered, Cancelled, Refunded
const CANCELLED_STATUS = 7;

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  const [targetStatus, setTargetStatus] = useState("");
  const [note, setNote] = useState("");
  const [pendingStatus, setPendingStatus] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [actionError, setActionError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    let active = true;
    getOrderDetail(id)
      .then((data) => {
        if (active) setOrder(data);
      })
      .catch(() => {
        if (active) setError("Could not load this order.");
      });
    return () => {
      active = false;
    };
  }, [id]);

  const isTerminal = order ? TERMINAL_STATUSES.includes(order.status) : false;

  const statusOptions = useMemo(
    () =>
      Object.entries(OrderStatusLabels)
        .map(([value, label]) => ({ value: Number(value), label }))
        .filter((option) => !order || option.value !== order.status),
    [order]
  );

  async function applyStatusChange(status) {
    setUpdating(true);
    setActionError("");
    try {
      const updated = await updateOrderStatus(id, status, note.trim() || undefined);
      setOrder(updated);
      setNote("");
      setTargetStatus("");
      setSuccessMessage(`Order status updated to ${OrderStatusLabels[status]}.`);
    } catch (err) {
      setActionError(err.response?.data?.message || err.response?.data || "Could not update the order status.");
    } finally {
      setUpdating(false);
      setPendingStatus(null);
    }
  }

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!order) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin/orders")} sx={{ mb: 2 }}>
        Back to Orders
      </Button>

      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4">{order.orderNumber}</Typography>
          <Typography variant="body2" color="text.secondary">
            Placed {formatDate(order.createdAt)}
          </Typography>
        </Box>
        <Chip label={OrderStatusLabels[order.status]} color={OrderStatusColors[order.status]} sx={{ fontWeight: 600 }} />
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Items
            </Typography>
            <List disablePadding>
              {order.items.map((item, idx) => (
                <ListItem key={idx} disableGutters divider={idx < order.items.length - 1}>
                  <ListItemAvatar>
                    <Avatar src={item.productImageUrl || undefined} variant="rounded" />
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.productName}
                    secondary={item.variantName ? `${item.variantName} • Qty ${item.quantity}` : `Qty ${item.quantity}`}
                  />
                  <Typography fontWeight={600}>{formatCurrency(item.unitPrice * item.quantity)}</Typography>
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={0.75}>
              <SummaryRow label="Subtotal" value={formatCurrency(order.subTotal)} />
              <SummaryRow label="Delivery Fee" value={formatCurrency(order.deliveryFee)} />
              <SummaryRow label="Discount" value={`- ${formatCurrency(order.discountAmount)}`} />
              <SummaryRow label="Total" value={formatCurrency(order.totalAmount)} bold />
            </Stack>
            {order.discountAmount > 0 && order.couponDiscountType === 3 && (
              <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 1 }}>
                Delivery was free for the customer — the courier was still paid the full delivery
                fee for this order.
              </Typography>
            )}
          </Paper>

          <Paper sx={{ p: 2.5 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Status History
            </Typography>
            <Stepper orientation="vertical">
              {order.statusHistory.map((h, idx) => (
                <Step key={idx} active completed>
                  <StepLabel>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" fontWeight={600}>
                        {OrderStatusLabels[h.status]}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(h.changedAt)}
                      </Typography>
                    </Stack>
                    {h.note && (
                      <Typography variant="caption" color="text.secondary">
                        {h.note}
                      </Typography>
                    )}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Order Status
            </Typography>
            {isTerminal ? (
              <Typography variant="body2" color="text.secondary">
                This order is {OrderStatusLabels[order.status].toLowerCase()} and can no longer be changed.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {actionError && (
                  <Alert severity="error" onClose={() => setActionError("")}>
                    {actionError}
                  </Alert>
                )}
                <FormControl size="small" fullWidth>
                  <InputLabel id="order-status-select-label">New status</InputLabel>
                  <Select
                    labelId="order-status-select-label"
                    label="New status"
                    value={targetStatus}
                    onChange={(e) => setTargetStatus(e.target.value)}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Note (optional)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />
                <Button
                  variant="contained"
                  disabled={targetStatus === "" || updating}
                  onClick={() => setPendingStatus(targetStatus)}
                >
                  Update Status
                </Button>
                <Divider />
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  disabled={updating}
                  onClick={() => setPendingStatus(CANCELLED_STATUS)}
                >
                  Cancel Order
                </Button>
              </Stack>
            )}
          </Paper>

          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Customer
            </Typography>
            <Stack spacing={1}>
              <DetailRow label="Name" value={order.customerName} />
              <DetailRow label="Phone" value={order.customerPhone || "-"} />
            </Stack>
          </Paper>

          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Store
            </Typography>
            <Stack spacing={1}>
              <DetailRow label="Name" value={order.storeName} />
            </Stack>
          </Paper>

          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Delivery Address
            </Typography>
            <Typography variant="body2">
              {order.addressStreet} {order.addressBuildingNumber}
              {order.addressApartmentNumber ? `, Apt ${order.addressApartmentNumber}` : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {order.addressCity}
            </Typography>
            {order.addressAdditionalDetails && (
              <Typography variant="caption" color="text.secondary">
                {order.addressAdditionalDetails}
              </Typography>
            )}
          </Paper>

          {order.deliveryStatus != null && (
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Delivery
              </Typography>
              <Stack spacing={1}>
                <DetailRow label="Status" value={DeliveryStatusLabels[order.deliveryStatus]} />
                <DetailRow label="Driver" value={order.driverName || "Not yet assigned"} />
              </Stack>
            </Paper>
          )}
        </Grid>
      </Grid>

      <Dialog open={pendingStatus !== null} onClose={() => (updating ? null : setPendingStatus(null))}>
        <DialogTitle>
          {pendingStatus === CANCELLED_STATUS ? "Cancel this order?" : "Change order status?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingStatus !== null &&
              `This will set order ${order.orderNumber} to "${OrderStatusLabels[pendingStatus]}". This ${
                TERMINAL_STATUSES.includes(Number(pendingStatus)) ? "cannot be undone." : "will notify the customer."
              }`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingStatus(null)} disabled={updating}>
            Back
          </Button>
          <Button
            variant="contained"
            color={pendingStatus === CANCELLED_STATUS ? "error" : "primary"}
            onClick={() => applyStatusChange(Number(pendingStatus))}
            disabled={updating}
          >
            {updating ? "Updating..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage("")}
        message={successMessage}
      />
    </Box>
  );
}

function SummaryRow({ label, value, bold }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color={bold ? "text.primary" : "text.secondary"} fontWeight={bold ? 700 : 400}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={bold ? 700 : 400}>
        {value}
      </Typography>
    </Stack>
  );
}

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
    </Stack>
  );
}
